import { eventIterator, oc } from "@orpc/contract";
import { z } from "zod";
import { OperatorStepSchema } from "@erp/shared-types";

export const RecordedOperatorStepSchema = z.object({
  id: z.uuid(),
  agentRunId: z.uuid(),
  taskId: z.uuid().nullable(),
  a2aTaskId: z.string().nullable(),
  createdAt: z.coerce.date(),
  step: OperatorStepSchema,
  screenshotUrls: z.array(z.string().nullable()),
});
export type RecordedOperatorStep = z.infer<typeof RecordedOperatorStepSchema>;

// --- Shared Schemas ---

export const SemanticModelNameSchema = z.enum([
  "fast",
  "balanced",
  "smart",
  "reasoning",
]);

export const AgentStopTypeSchema = z.enum(["MAX_STEPS", "NO_MORE_TASKS"]);

export const AgentModelConfigSchema = z.object({
  model: SemanticModelNameSchema.optional(),
  maxSteps: z.number().min(1).max(1000).optional(),
  stopType: AgentStopTypeSchema.optional(),
  /** Consecutive tool-calling steps without progress before a run fails as `no_progress`. */
  noProgressStepLimit: z.number().int().min(1).max(100).optional(),
});

export const AgentInputFieldDefSchema = z.object({
  fieldName: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  socketType: z.string(),
  required: z.boolean(),
  sortOrder: z.number(),
});

export const AgentOutputFieldDefSchema = z.object({
  fieldName: z.string(),
  displayName: z.string(),
  description: z.string(),
  socketType: z.string(),
  sortOrder: z.number(),
});

// Persisted definitions and historical versions may contain legacy names.
// Restrict new writes without making those records unreadable.
const AgentOutputFieldWriteSchema = AgentOutputFieldDefSchema.extend({
  fieldName: z
    .string()
    .refine(
      (name) => !["_meta", "_taskOutcome"].includes(name),
      "Reserved agent output field",
    ),
});

export const AgentToolDefSchema = z.object({
  toolType: z.string(),
  config: z.record(z.string(), z.unknown()).optional(),
});

export const ToolCategorySchema = z.enum([
  "Email",
  "AP Invoices",
  "Sales Orders",
  "Purchase Orders",
  "Matching",
  "Customers",
  "Payments",
  "Utility",
  "Tasks",
  "Remote Desktop",
  "AI & Agents",
  "Data Access",
  "Integration",
  "Ship & Debit",
  "Workbooks",
]);

export const ToolPermissionSchema = z.object({
  action: z.string(),
  subject: z.string(),
});

export const CatalogToolMetadataSchema = z.object({
  type: z.string(),
  name: z.string(),
  description: z.string(),
  category: ToolCategorySchema,
  readOnly: z.boolean(),
  lifecycleManaged: z.boolean().optional(),
  // False for resource-lifecycle tools the task loop invokes directly (open/close
  // browser): hidden from the grantable catalog. Defaults to grantable when omitted.
  agentGrantable: z.boolean().optional(),
  // True when the tool can be selected as a `tool`-type setup/teardown phase.
  loopPhaseTool: z.boolean().optional(),
  requiredPermissions: z.array(ToolPermissionSchema).optional(),
  deprecated: z
    .object({
      reason: z.string(),
      replacedBy: z.string().optional(),
    })
    .optional(),
  visibility: z
    .object({
      visibility: z.enum(["visible", "hidden", "restricted", "disabled"]),
      predicateResults: z
        .array(
          z.array(
            z.object({
              type: z.string(),
              passed: z.boolean(),
              detail: z.string(),
            }),
          ),
        )
        .optional(),
      disabledReason: z.string().optional(),
    })
    .optional(),
});

// --- Agent Run Schemas ---

export const AgentRunStatusSchema = z.enum([
  "running",
  "completed",
  "failed",
  "blocked",
]);

export const KernelTelemetrySummarySchema = z.object({
  eventCount: z.number(),
  byCategory: z.record(z.string(), z.number()),
  notable: z.object({
    crashes: z.number(),
    disconnects: z.number(),
    captchaFailures: z.number(),
    oomKills: z.number(),
  }),
});

export const KernelTelemetryEnvelopeSchema = z
  .object({
    seq: z.number(),
    event: z
      .object({
        category: z.string(),
        type: z.string(),
      })
      .passthrough(),
  })
  .passthrough();

/**
 * Why a run started. Stamped by the durable launcher (`agentDefinitions.launchRun`
 * and the schedule / event / workflow kickoffs built on it); null on runs from the
 * legacy `startRun` path or a workflow `background_agent` step.
 */
export const AgentRunTriggerKindSchema = z.enum([
  "manual",
  "schedule",
  "event",
  "api",
  "task_queue",
  "workflow",
]);
export type AgentRunTriggerKind = z.infer<typeof AgentRunTriggerKindSchema>;

export const AgentRunMetaSchema = z.object({
  confidence: z.number(),
  reasoning: z.string(),
  unresolvableFields: z.array(
    z.object({
      field: z.string(),
      reason: z.string(),
    }),
  ),
});

/**
 * Narrows `agentDefinitions.listRuns`. An object so later filters (a date
 * range, run categories) slot in beside `hideSkipped` without a new shape.
 */
export const AgentRunListFilterSchema = z.object({
  /**
   * Leave out runs the start gate refused while the agent was merely waiting
   * (`completed` with `finishReason` `not_started`). Refusals that failed —
   * a paused resource, a lease held by a finished run — stay: a person has
   * to act on those. So does a run a person started by hand, so pressing Run
   * always leaves a visible answer.
   */
  hideSkipped: z
    .boolean()
    .optional()
    .describe(
      "Leave out runs that did not start because the agent was only waiting (start conditions not met or its resource busy). Runs a person started and refusals that failed stay.",
    ),
});
export type AgentRunListFilter = z.infer<typeof AgentRunListFilterSchema>;

export const AgentRunSummarySchema = z.object({
  id: z.uuid(),
  agentDefinitionId: z.uuid().nullable(),
  status: AgentRunStatusSchema,
  model: z.string().nullable().optional(),
  llmSteps: z.number().nullable().optional(),
  totalInputTokens: z.number().nullable().optional(),
  totalOutputTokens: z.number().nullable().optional(),
  totalReasoningTokens: z.number().nullable().optional(),
  totalCacheReadTokens: z.number().nullable().optional(),
  totalCacheWriteTokens: z.number().nullable().optional(),
  finishReason: z.string().nullable().optional(),
  /**
   * On a task-loop run the start gate refused (`finishReason` `not_started`):
   * the coordinator's reason, e.g. `resources_unavailable`. Null otherwise.
   */
  notStartedReason: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  triggerKind: AgentRunTriggerKindSchema.nullable().optional(),
  /** The Temporal execution owning a launcher-started run; null otherwise. */
  temporalWorkflowId: z.string().nullable().optional(),
  /** The definition version the run executed; null before versioning. */
  agentDefinitionVersionId: z.uuid().nullable().optional(),
  /** On a `blocked` run: the published version its retry found instead. */
  blockedByDefinitionVersionId: z.uuid().nullable().optional(),
  /** Set while a "stop after current task" request is pending on the run. */
  stopAfterTaskRequestedAt: z.iso.datetime().nullable().optional(),
  stopAfterTaskRequestedBy: z.uuid().nullable().optional(),
  /** Set once the loop acted on the request; it can't be withdrawn after. */
  stopAfterTaskAcceptedAt: z.iso.datetime().nullable().optional(),
  startedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable().optional(),
  createdAt: z.iso.datetime(),
});

export const AgentMessageResponseSchema = z.object({
  id: z.uuid(),
  agentRunId: z.uuid(),
  stepNumber: z.number().nullable().optional(),
  role: z.enum(["system", "user", "assistant", "tool"]),
  text: z.string().nullable().optional(),
  reasoning: z.string().nullable().optional(),
  finishReason: z.string().nullable().optional(),
  // Concrete model id that produced this message (e.g. "claude-sonnet-4-6").
  // Set per-message so a subagent turn (e.g. an operator iteration running its
  // own model) reports the model it actually used, distinct from the parent
  // run's model. Null for messages whose model wasn't captured.
  model: z.string().nullable().optional(),
  inputTokens: z.number().nullable().optional(),
  outputTokens: z.number().nullable().optional(),
  reasoningTokens: z.number().nullable().optional(),
  cacheReadTokens: z.number().nullable().optional(),
  cacheWriteTokens: z.number().nullable().optional(),
  createdAt: z.iso.datetime(),
});

export const ToolCallRunResponseSchema = z.object({
  id: z.uuid(),
  agentRunId: z.uuid(),
  agentMessageId: z.uuid().nullable().optional(),
  toolType: z.string().nullable().optional(),
  toolCallId: z.string().nullable().optional(),
  status: z.string(),
  inputData: z.record(z.string(), z.unknown()),
  outputData: z.record(z.string(), z.unknown()).nullable().optional(),
  screenshotDocumentId: z.uuid().nullable().optional(),
  screenshotSignedUrl: z.string().nullable().optional(),
  startedAt: z.iso.datetime().nullable().optional(),
  completedAt: z.iso.datetime().nullable().optional(),
  duration: z.number().nullable().optional(),
  stopReason: z.string().nullable().optional(),
  /**
   * When this tool call is a task-loop phase that spawned a child agent run,
   * the child run's id. The trace nests the child run's timeline beneath it.
   */
  childAgentRunId: z.uuid().nullable().optional(),
  /**
   * When this tool call is a task-loop phase that launched a setup/teardown
   * workflow, the child WorkflowRun id (drill-in to the workflow trace).
   */
  childWorkflowRunId: z.uuid().nullable().optional(),
  /**
   * Workflow-step provenance for tool calls that ran inside a workflow step
   * (e.g. computer-use operator iterations from a remote-desktop step). The
   * workflow run the iteration belongs to, and the StepRun it nests under.
   * Null for plain agent tool calls. Lets a workflow run's trace render the
   * iteration under the step it ran inside, while `agentRunId` still
   * attributes it to the orchestrating agent run.
   */
  workflowRunId: z.uuid().nullable().optional(),
  parentStepRunId: z.uuid().nullable().optional(),
});

/**
 * One step of the workflow a task-loop setup/teardown phase launched (e.g. "Login to Remote
 * Desktop"), so the run's trace can fold the phase open into the steps it ran, each with its real
 * start and duration.
 */
export const WorkflowPhaseStepSchema = z.object({
  /** The workflow step's StepRun id — what an operator row's `parentToolCallId` points at. */
  id: z.uuid(),
  /** The loop's phase StepRun that launched the workflow. */
  phaseStepRunId: z.uuid(),
  name: z.string(),
  status: z.string(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  duration: z.number().nullable(),
});
export type WorkflowPhaseStep = z.infer<typeof WorkflowPhaseStepSchema>;

// --- Response Schemas ---

/**
 * A configuration problem that will make a run fail, surfaced ahead of time. Advisory only:
 * saving is never blocked, since a user may be mid-setup.
 */
export const AgentConfigWarningSchema = z.object({
  /** The agent can log in to a remote desktop but has nothing to log in with. */
  code: z.enum(["remote_desktop_no_login_source"]),
  message: z.string(),
});

export const AgentDefinitionResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  createdBy: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  templateType: z.string().nullable().optional(),
  systemPrompt: z.string(),
  modelConfig: AgentModelConfigSchema,
  config: z.record(z.string(), z.unknown()),
  inputFields: z.array(AgentInputFieldDefSchema),
  outputFields: z.array(AgentOutputFieldDefSchema),
  tools: z.array(AgentToolDefSchema),
  isActive: z.boolean(),
  /** Org namespace this agent is grouped under (`namespaces.*`), if any. */
  namespaceId: z.uuid().nullable().optional(),
  /** The version currently used by agent runs. Null only until the version backfill runs. */
  pinnedVersionId: z.uuid().nullable(),
  version: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Set when the definition is archived (soft-deleted, kept for audit). */
  deletedAt: z.iso.datetime().nullable().optional(),
  /** Run-time failures predictable from this configuration. Only set by `get`. */
  configWarnings: z.array(AgentConfigWarningSchema).optional(),
});

export const AgentDefinitionSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  templateType: z.string().nullable().optional(),
  isActive: z.boolean(),
  /** Org namespace this agent is grouped under (`namespaces.*`), if any. */
  namespaceId: z.uuid().nullable().optional(),
  version: z.number(),
  toolCount: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Set when the definition is archived (soft-deleted, kept for audit). */
  deletedAt: z.iso.datetime().nullable().optional(),
});

export const AgentTemplateResponseSchema = z.object({
  templateType: z.string(),
  name: z.string(),
  description: z.string(),
  requiredTools: z.array(z.string()),
  availableTools: z.array(z.string()),
  defaultInputFields: z.array(AgentInputFieldDefSchema),
  defaultOutputFields: z.array(AgentOutputFieldDefSchema),
  defaultModelConfig: AgentModelConfigSchema,
});

// --- Request Schemas ---

export const CreateAgentDefinitionRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  templateType: z.string().optional(),
  systemPrompt: z.string().optional(),
  modelConfig: AgentModelConfigSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  inputFields: z.array(AgentInputFieldDefSchema).optional(),
  outputFields: z.array(AgentOutputFieldWriteSchema).optional(),
  tools: z.array(AgentToolDefSchema).optional(),
  isActive: z.boolean().optional(),
  /** Org namespace (`namespaces.*`) to file the new agent under. */
  namespaceId: z.uuid().nullable().optional(),
});

export const UpdateAgentDefinitionRequestSchema = z.object({
  agentDefinitionId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  /** Org namespace grouping (`namespaces.*`); null clears it. */
  namespaceId: z.uuid().nullable().optional(),
});

export const CloneAgentDefinitionRequestSchema = z.object({
  agentDefinitionId: z.uuid(),
  /** Name for the copy. Defaults to the source name with a "(copy)" suffix. */
  name: z.string().trim().min(1).max(255).optional(),
});

// --- Contracts ---

/**
 * One entry in a definition's version history.
 *
 * `changelog` is already display-ready: the backend unwraps the mechanical JSON it stores before
 * the AI pass lands, so a consumer never has to know that two representations share the column.
 */
export const AgentDefinitionVersionSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  status: z.enum(["draft", "finalized"]),
  parentVersionId: z.uuid().nullable(),
  changelog: z.string().nullable(),
  /** Whether this is the version the agent currently runs. */
  isPinned: z.boolean(),
  createdBy: z.uuid().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

/** A version plus the editable behavior snapshot stored on it. */
export const AgentDefinitionVersionDetailSchema =
  AgentDefinitionVersionSchema.extend({
    systemPrompt: z.string(),
    modelConfig: AgentModelConfigSchema,
    config: z.record(z.string(), z.unknown()),
    inputFields: z.array(AgentInputFieldDefSchema),
    outputFields: z.array(AgentOutputFieldDefSchema),
    tools: z.array(AgentToolDefSchema),
    /** Run-time failures predictable from this version's configuration. */
    configWarnings: z.array(AgentConfigWarningSchema).optional(),
  });

export const UpdateAgentDefinitionDraftRequestSchema = z.object({
  agentDefinitionId: z.uuid(),
  versionId: z.uuid(),
  systemPrompt: z.string().optional(),
  modelConfig: AgentModelConfigSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  inputFields: z.array(AgentInputFieldDefSchema).optional(),
  outputFields: z.array(AgentOutputFieldWriteSchema).optional(),
  tools: z.array(AgentToolDefSchema).optional(),
});

export const AgentDefinitionStaleBaseWarningSchema = z.object({
  kind: z.literal("stale_base"),
  draftParentVersionId: z.uuid().nullable(),
  draftParentVersionNumber: z.number().int().nullable(),
  currentPublishedVersionId: z.uuid().nullable(),
  currentPublishedVersionNumber: z.number().int().nullable(),
  message: z.string(),
});

export const FinalizeAgentDefinitionVersionResponseSchema =
  z.discriminatedUnion("ok", [
    z.object({
      ok: z.literal(true),
      definition: AgentDefinitionResponseSchema,
      version: AgentDefinitionVersionDetailSchema,
    }),
    z.object({
      ok: z.literal(false),
      warning: AgentDefinitionStaleBaseWarningSchema,
    }),
  ]);

export const PublishAgentDefinitionVersionResponseSchema = z.discriminatedUnion(
  "ok",
  [
    z.object({
      ok: z.literal(true),
      definition: AgentDefinitionResponseSchema,
    }),
    z.object({
      ok: z.literal(false),
      warning: AgentDefinitionStaleBaseWarningSchema,
    }),
  ],
);

/**
 * Why the task-loop coordinator would (or would not) start an agent right now.
 *
 * A paused required resource is not its own value. It is reported as
 * `resources_unavailable` with kind `blocking`, so adding that start-gate
 * outcome does not extend this response enum.
 */
export const AgentStartReadinessReasonSchema = z.enum([
  "ready",
  "task_management_disabled",
  "no_start_conditions_configured",
  "resources_unavailable",
  "start_conditions_not_met",
]);

/**
 * `startable`: the coordinator would start the agent now (or task management is off).
 * `blocking`: a misconfiguration or a paused resource; the agent never starts until a person fixes it.
 * `transient`: a temporary wait (resources busy, conditions not yet met).
 */
export const AgentStartReadinessKindSchema = z.enum([
  "startable",
  "blocking",
  "transient",
]);

/**
 * One active lease on a leaseable resource and the agent run that took it.
 * `runStatus` other than `running` (or null, when the run row is gone) means
 * the lease outlived its run: nobody is using the slot, yet it stays taken
 * until `expiresAt` unless an administrator releases it.
 */
export const ResourceLeaseHolderSchema = z.object({
  leaseId: z.uuid(),
  agentRunId: z.uuid(),
  agentDefinitionId: z.uuid().nullable(),
  agentName: z.string().nullable(),
  runStatus: AgentRunStatusSchema.nullable(),
  runCompletedAt: z.iso.datetime().nullable(),
  leasedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
});

/** A required resource with no free slot, and the leases filling it. */
export const AgentStartHeldResourceSchema = z.object({
  resourceId: z.uuid(),
  resourceName: z.string(),
  maxConcurrent: z.number(),
  holders: z.array(ResourceLeaseHolderSchema),
});

export const AgentStartReadinessSchema = z.object({
  reason: AgentStartReadinessReasonSchema,
  kind: AgentStartReadinessKindSchema,
  /**
   * For `resources_unavailable` with kind `transient`: which required resources
   * have every slot leased, and by which runs. Absent for every other reason.
   */
  heldResources: z.array(AgentStartHeldResourceSchema).optional(),
});

export const agentDefinitionContracts = {
  list: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
        /** Include archived (soft-deleted) definitions in the results. */
        includeArchived: z.boolean().optional(),
      }),
    )
    .output(
      z.object({
        definitions: z.array(AgentDefinitionSummarySchema),
        total: z.number(),
      }),
    )
    .route({
      method: "GET",
      summary: "List background agents",
      description:
        "Pages through the org's background agent definitions with name, template, active flag and namespace, optionally including archived agents.",
    }),

  get: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(AgentDefinitionResponseSchema)
    .route({
      method: "GET",
      summary: "Get a background agent",
      description:
        "Returns one background agent definition: system prompt, model settings, config, input and output fields, granted tools, active flag and published version.",
    }),

  /** Read-only: whether the task-loop coordinator would start this agent now, and why. */
  getStartReadiness: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(AgentStartReadinessSchema)
    .route({
      method: "GET",
      summary: "Check whether a background agent can start",
      description:
        "Reports whether the task coordinator would start this agent now and why not: a blocking misconfiguration or paused resource, or a temporary wait. Read-only.",
    }),

  create: oc
    .input(CreateAgentDefinitionRequestSchema)
    .output(AgentDefinitionResponseSchema)
    .route({
      method: "POST",
      summary: "Create a background agent",
      description:
        "Creates a background agent definition with name, prompt, model settings, input and output fields, tools, template and namespace, plus its first version.",
    }),

  update: oc
    .input(UpdateAgentDefinitionRequestSchema)
    .output(AgentDefinitionResponseSchema)
    .route({
      method: "PUT",
      summary: "Rename or activate a background agent",
      description:
        "Changes a background agent's name, description, active flag or namespace. Prompt, tools and fields change through versioned drafts instead.",
    }),

  delete: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(
      z.object({
        success: z.boolean(),
        /** Number of open tasks whose agent assignment was cleared by the archive. */
        clearedTaskCount: z.number().optional(),
      }),
    )
    .route({
      method: "DELETE",
      summary: "Archive a background agent",
      description:
        "Archives a background agent definition (kept for audit) and clears it from open tasks it was assigned to; returns how many tasks were cleared.",
    }),

  clone: oc
    .input(CloneAgentDefinitionRequestSchema)
    .output(AgentDefinitionResponseSchema)
    .route({
      method: "POST",
      summary: "Clone a background agent",
      description:
        "Creates a new background agent from another agent's published configuration and resources, so several copies can run at the same time.",
    }),

  restore: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(AgentDefinitionResponseSchema)
    .route({
      method: "POST",
      summary: "Restore an archived background agent",
      description:
        "Brings an archived background agent definition back so it can be used and run again.",
    }),

  listToolCatalog: oc
    .input(z.object({}))
    .output(
      z.object({
        tools: z.array(CatalogToolMetadataSchema),
      }),
    )
    .route({
      method: "GET",
      summary: "List tools agents can use",
      description:
        "Lists the tools that can be granted to background agents, with category, read-only flag, required permissions, deprecation and visibility.",
    }),

  listTemplates: oc
    .input(z.object({}))
    .output(
      z.object({
        templates: z.array(AgentTemplateResponseSchema),
      }),
    )
    .route({
      method: "GET",
      summary: "List background agent templates",
      description:
        "Lists the starter templates for new background agents with their required and available tools, default fields and default model settings.",
    }),

  listRuns: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        limit: z.number().optional(),
        offset: z.number().optional(),
        filter: AgentRunListFilterSchema.optional().describe(
          "Narrows the list. hideSkipped: true leaves out runs that did not start because the agent was only waiting; total then counts what is left and hiddenCount what was left out.",
        ),
      }),
    )
    .output(
      z.object({
        runs: z.array(AgentRunSummarySchema),
        /** Runs matching the filter (all of them, not just this page). */
        total: z.number(),
        /**
         * Runs the filter left out; 0 when nothing is filtered. Null from a
         * backend that predates the field (render as unknown, not as 0).
         */
        hiddenCount: z
          .number()
          .nullable()
          .default(null)
          .describe("Runs the filter left out (0 without a filter)."),
        /**
         * When the newest run of any kind started, filter or not: a skipped
         * check still says when the agent last looked for work. Null when the
         * agent never ran, or from a backend that predates the field.
         */
        latestAttemptAt: z.iso
          .datetime()
          .nullable()
          .default(null)
          .describe(
            "When the newest run of any kind started, skipped runs included.",
          ),
      }),
    )
    .route({
      method: "GET",
      summary: "List a background agent's runs",
      description:
        "Pages through the runs of one background agent with status, trigger and timing. Set filter.hideSkipped to leave out runs that did not start because the agent was only waiting (start conditions not met, or its resource was busy).",
    }),

  getRunTimeline: oc
    .input(
      z.object({
        agentRunId: z.uuid(),
        includeOperatorSteps: z.boolean().optional(),
      }),
    )
    .output(
      z.object({
        run: AgentRunSummarySchema,
        operatorSteps: z.array(RecordedOperatorStepSchema).optional(),
        inspectionRecording: z
          .object({
            agentRunId: z.uuid(),
            documentId: z.uuid(),
            stoppedAt: z.coerce.date().nullable(),
            isComplete: z.boolean(),
          })
          .nullable()
          .optional(),
        messages: z.array(AgentMessageResponseSchema),
        toolCalls: z.array(ToolCallRunResponseSchema),
        // The steps of every workflow this run's task-loop phases launched, oldest first.
        workflowPhaseSteps: z.array(WorkflowPhaseStepSchema).optional(),
        // Screenshot-bearing action tool calls for this run AND its descendant
        // child runs, ordered by startedAt. Task-loop children audit their
        // remote-desktop actions under their own run id while the recording is
        // bound to the parent loop run, so the recording timeline markers and
        // screenshot gallery would be empty if they only read `toolCalls`
        // (which holds just this run's own steps, for the trace view).
        recordingToolCalls: z.array(ToolCallRunResponseSchema),
        recordingDocumentId: z.string().uuid().nullable(),
        kernelTelemetryDocumentId: z.string().uuid().nullable(),
        kernelTelemetrySummary: KernelTelemetrySummarySchema.nullable(),
        // The screenshot captured at the start of the run (before the first
        // action). Lets the UI render each click on the frame the agent saw.
        initialScreenshotDocumentId: z.string().uuid().nullable(),
        initialScreenshotSignedUrl: z.string().nullable(),
        // Live human-takeover state over the run subtree (AI-244): non-null while an
        // operator run is parked awaiting a human ("paused") or a resume is recorded but
        // not yet picked up by the draining process ("resume_pending"). Rides this
        // already-polled read because the pause park may live in ANOTHER process (a
        // workflow worker or a different replica), so the replica-local live stream
        // can't be the only source of the monitor's composer.
        operatorTakeover: z
          .object({
            state: z.enum(["paused", "resume_pending"]),
            question: z.string().nullable(),
            pausedAt: z.iso.datetime(),
          })
          .nullable(),
      }),
    )
    .route({
      method: "GET",
      summary: "Get a background agent run's timeline",
      description:
        "Returns one agent run with its messages, tool calls, workflow steps, screen recording and screenshots, browser health summary, and any human-takeover pause.",
    }),

  getKernelTelemetry: oc
    .input(
      z.object({
        agentRunId: z.uuid(),
        cursor: z.string().optional(),
        limit: z.number().int().min(1).max(100).optional(),
        category: z.array(z.string()).optional(),
      }),
    )
    .output(
      z.object({
        events: z.array(KernelTelemetryEnvelopeSchema),
        nextCursor: z.string().nullable(),
        summary: KernelTelemetrySummarySchema.nullable(),
        source: z.enum(["document", "kernel", "none"]),
      }),
    )
    .route({
      method: "GET",
      summary: "Get browser session telemetry for a run",
      description:
        "Pages through browser-session events for an agent run (crashes, disconnects, captcha failures, out-of-memory), filterable by category, with a summary.",
    }),

  subscribeKernelTelemetry: oc
    .input(
      z.object({
        agentRunId: z.uuid(),
        lastSeq: z.number().optional(),
      }),
    )
    .output(eventIterator(KernelTelemetryEnvelopeSchema))
    .route({
      method: "POST",
      summary: "Stream browser session telemetry for a run",
      description:
        "Streams live browser-session events for a running agent, resuming after the last sequence number seen.",
    }),

  cancelRun: oc
    .input(z.object({ agentRunId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "POST",
      summary: "Cancel a background agent run",
      description:
        "Stops a running background agent run and its child runs, closes their browser sessions, and cancels the workflow run that launched it, if any.",
    }),

  // Graceful stop for a task loop: the run finishes the task it is on, runs
  // teardown, and completes instead of claiming another. `stop: false`
  // withdraws the request. Throws BAD_REQUEST when the run is not running.
  setStopAfterCurrentTask: oc
    .input(z.object({ agentRunId: z.uuid(), stop: z.boolean() }))
    .output(z.object({ ok: z.boolean() }))
    .route({
      method: "POST",
      summary: "Stop an agent run after its current task",
      description:
        "Asks a running task-loop agent run to finish the task it is working on and then stop, instead of picking up the next task. Pass stop=false to withdraw the request.",
    }),

  // Human takeover of a live operator run (agent-monitor, AI-244). pauseOperatorRun asks the
  // running operator to pause at its next safe step; then EITHER instructOperatorRun (send a
  // typed instruction) OR resumeOperatorRun (hand control back after driving the desktop) resumes
  // the SAME run. The agentRunId is the MONITORED run — the backend resolves the task-loop child
  // actually driving the browser (AI-436). All three route cross-process: pause via the durable
  // a2aCancel handle, instruct/resume via the ai_operator_takeovers rendezvous row (so they reach
  // a run draining in the workflow worker or on another replica). All three throw BAD_REQUEST
  // when there is no live/paused operator run to act on, so the UI can surface the miss.
  pauseOperatorRun: oc
    .input(z.object({ agentRunId: z.uuid() }))
    .output(z.object({ ok: z.boolean() }))
    .route({
      method: "POST",
      summary: "Pause a browser agent for takeover",
      description:
        "Asks a running browser-driving agent to pause at its next safe step so a person can take over or send instructions.",
    }),

  instructOperatorRun: oc
    .input(
      z.object({
        agentRunId: z.uuid(),
        instruction: z.string().min(1).max(2000),
      }),
    )
    .output(z.object({ ok: z.boolean() }))
    .route({
      method: "POST",
      summary: "Send an instruction to a paused agent",
      description:
        "Sends a typed instruction to a paused browser-driving agent run and resumes it with that guidance.",
    }),

  resumeOperatorRun: oc
    .input(z.object({ agentRunId: z.uuid() }))
    .output(z.object({ ok: z.boolean() }))
    .route({
      method: "POST",
      summary: "Resume a paused browser agent",
      description:
        "Hands control back to a paused browser-driving agent run after a person drove the desktop, resuming the same run.",
    }),

  startRun: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        inputData: z.record(z.string(), z.unknown()).optional().default({}),
      }),
    )
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "POST",
      summary: "Start a background agent run (legacy)",
      description:
        "Starts a background agent with optional input data in the web process without tracking it durably; superseded by launching a run.",
    }),

  /**
   * Durable kickoff: creates the run row, hands execution to a Temporal worker,
   * and returns the run id immediately (poll `getRunTimeline` for progress).
   * Supersedes `startRun`, which fires the agent in the web process and forgets
   * it; `startRun` stays until this path is proven and then goes away.
   */
  launchRun: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        inputData: z.record(z.string(), z.unknown()).optional().default({}),
      }),
    )
    .output(
      z.object({
        agentRunId: z.uuid(),
        temporalWorkflowId: z.string(),
      }),
    )
    .route({
      method: "POST",
      summary: "Launch a background agent run",
      description:
        "Starts a durable run of a background agent with optional input data and returns the run id immediately; progress is read from the run timeline.",
    }),

  // --- Version history (AI-458) ---

  listVersions: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(z.object({ versions: z.array(AgentDefinitionVersionSchema) }))
    .route({
      method: "GET",
      summary: "List a background agent's versions",
      description:
        "Lists every version of a background agent definition with number, draft or finalized status, parent version and changelog.",
    }),

  getVersion: oc
    .input(z.object({ agentDefinitionId: z.uuid(), versionId: z.uuid() }))
    .output(AgentDefinitionVersionDetailSchema)
    .route({
      method: "GET",
      summary: "Get a background agent version",
      description:
        "Returns one version of a background agent with its prompt, model settings, config, fields and tools as of that version.",
    }),

  createDraft: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        sourceVersionId: z.uuid().optional(),
      }),
    )
    .output(
      z.object({
        version: AgentDefinitionVersionDetailSchema,
        resumed: z.boolean(),
      }),
    )
    .route({
      method: "POST",
      summary: "Start a draft of a background agent",
      description:
        "Opens an editable draft version of a background agent, copied from the published or a chosen version; resumes an existing draft if one is open.",
    }),

  updateDraft: oc
    .input(UpdateAgentDefinitionDraftRequestSchema)
    .output(AgentDefinitionVersionDetailSchema)
    .route({
      method: "PUT",
      summary: "Edit a background agent draft",
      description:
        "Saves changes to a draft version's system prompt, model settings, config, input and output fields or tools. Runs are unaffected until published.",
    }),

  finalizeVersion: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        versionId: z.uuid(),
        publish: z.boolean().optional().default(true),
        acknowledgeStaleBase: z.boolean().optional().default(false),
      }),
    )
    .output(FinalizeAgentDefinitionVersionResponseSchema)
    .route({
      method: "POST",
      summary: "Finalize a background agent draft",
      description:
        "Locks a draft version and, by default, publishes it so new runs use it; warns if another version was published since the draft began.",
    }),

  deleteDraft: oc
    .input(z.object({ agentDefinitionId: z.uuid(), versionId: z.uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "DELETE",
      summary: "Delete a background agent draft",
      description:
        "Discards an unfinalized draft version of a background agent.",
    }),

  /** Publish a finalized version, changing what subsequent agent runs use. */
  publishVersion: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        versionId: z.uuid(),
        acknowledgeStaleBase: z.boolean().optional().default(false),
      }),
    )
    .output(PublishAgentDefinitionVersionResponseSchema)
    .route({
      method: "POST",
      summary: "Publish a background agent version",
      description:
        "Makes a finalized version the one new runs of the background agent use; warns if it is based on an outdated version.",
    }),

  regenerateChangelog: oc
    .input(z.object({ agentDefinitionId: z.uuid(), versionId: z.uuid() }))
    .output(z.object({ changelog: z.string().nullable() }))
    .route({
      method: "POST",
      summary: "Regenerate a version's changelog",
      description:
        "Rewrites the AI-written changelog describing what changed in one background agent version, and returns it.",
    }),
} as const;

// --- Export types ---

export type AgentDefinitionResponse = z.infer<
  typeof AgentDefinitionResponseSchema
>;
export type AgentDefinitionSummary = z.infer<
  typeof AgentDefinitionSummarySchema
>;
export type AgentTemplateResponse = z.infer<typeof AgentTemplateResponseSchema>;
export type CreateAgentDefinitionRequest = z.infer<
  typeof CreateAgentDefinitionRequestSchema
>;
export type UpdateAgentDefinitionRequest = z.infer<
  typeof UpdateAgentDefinitionRequestSchema
>;
export type CatalogToolMetadata = z.infer<typeof CatalogToolMetadataSchema>;
export type AgentRunSummary = z.infer<typeof AgentRunSummarySchema>;
export type AgentMessageResponse = z.infer<typeof AgentMessageResponseSchema>;
export type ToolCallRunResponse = z.infer<typeof ToolCallRunResponseSchema>;
export type AgentDefinitionVersionEntry = z.infer<
  typeof AgentDefinitionVersionSchema
>;
export type AgentDefinitionVersionDetail = z.infer<
  typeof AgentDefinitionVersionDetailSchema
>;
export type UpdateAgentDefinitionDraftRequest = z.infer<
  typeof UpdateAgentDefinitionDraftRequestSchema
>;
export type AgentConfigWarning = z.infer<typeof AgentConfigWarningSchema>;
export type AgentDefinitionStaleBaseWarning = z.infer<
  typeof AgentDefinitionStaleBaseWarningSchema
>;
export type FinalizeAgentDefinitionVersionResponse = z.infer<
  typeof FinalizeAgentDefinitionVersionResponseSchema
>;
export type PublishAgentDefinitionVersionResponse = z.infer<
  typeof PublishAgentDefinitionVersionResponseSchema
>;
export type AgentStartReadinessReason = z.infer<
  typeof AgentStartReadinessReasonSchema
>;
export type AgentStartReadinessKind = z.infer<
  typeof AgentStartReadinessKindSchema
>;
export type AgentStartReadiness = z.infer<typeof AgentStartReadinessSchema>;
export type AgentStartHeldResource = z.infer<
  typeof AgentStartHeldResourceSchema
>;
export type ResourceLeaseHolder = z.infer<typeof ResourceLeaseHolderSchema>;
export type KernelTelemetrySummary = z.infer<
  typeof KernelTelemetrySummarySchema
>;
export type KernelTelemetryEnvelope = z.infer<
  typeof KernelTelemetryEnvelopeSchema
>;
